import { isLocked } from '../../lib/premiumLock';
import { calculateBasketMastery } from './masteryFilter';

const ROW_GLANCE_CAP = 4;
const MIX_TARGET_PER_TYPE = 2;

const toTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const toUpdatedAt = (basket) => toTime(basket?.updated_at);

/** Newest last_recalled_at across topics; 0 if none (counts as oldest). */
const basketLastRecalledAt = (basket) => {
  let newest = 0;
  (basket?.topics || []).forEach((topic) => {
    const time = toTime(topic?.last_recalled_at);
    if (time > newest) newest = time;
  });
  return newest;
};

const qualifiesForFirstSlot = (basket, { isPremium, isLoading }) => {
  if ((basket?.topics?.length || 0) < 2) return false;
  if (isLocked(basket, isPremium, isLoading)) return false;
  return true;
};

const compareForRow = (a, b) => {
  const pinnedA = Boolean(a?.is_pinned);
  const pinnedB = Boolean(b?.is_pinned);
  if (pinnedA !== pinnedB) return pinnedB - pinnedA;

  if (pinnedA && pinnedB) {
    const updatedDiff = toUpdatedAt(b) - toUpdatedAt(a);
    if (updatedDiff !== 0) return updatedDiff;
  }

  const recallDiff = basketLastRecalledAt(a) - basketLastRecalledAt(b);
  if (recallDiff !== 0) return recallDiff;

  const masteryDiff =
    calculateBasketMastery(a) - calculateBasketMastery(b);
  if (masteryDiff !== 0) return masteryDiff;

  const topicDiff = (b.topics?.length || 0) - (a.topics?.length || 0);
  if (topicDiff !== 0) return topicDiff;

  return (a?.name || '').localeCompare(b?.name || '', undefined, {
    sensitivity: 'base',
  });
};

const applyFirstSlotRule = (ordered, opts) => {
  if (ordered.length === 0) return ordered;
  if (qualifiesForFirstSlot(ordered[0], opts)) return ordered;

  const qualifyingIndex = ordered.findIndex((basket) =>
    qualifiesForFirstSlot(basket, opts)
  );
  if (qualifyingIndex <= 0) return ordered;

  const next = [...ordered];
  const [qualifying] = next.splice(qualifyingIndex, 1);
  next.unshift(qualifying);
  return next;
};

/**
 * Conversion mix: for free users, interleave locked (premium) baskets with
 * unlocked ones in the first four slots so template/premium baskets sit next
 * to free ones. Pinned keep the front; tail stays in sorted order.
 */
const applyFreeUserRowMix = (ordered, { isPremium, isLoading }) => {
  const pinnedPrefix = ordered
    .filter((basket) => Boolean(basket?.is_pinned))
    .slice(0, ROW_GLANCE_CAP);

  const pinnedIds = new Set(pinnedPrefix.map((basket) => basket.id));
  const rest = ordered.filter((basket) => !pinnedIds.has(basket.id));

  const unlocked = rest.filter(
    (basket) => !isLocked(basket, isPremium, isLoading)
  );
  const locked = rest.filter((basket) =>
    isLocked(basket, isPremium, isLoading)
  );

  const front = [...pinnedPrefix];
  let preferUnlocked = true;

  const countInFront = (wantLocked) =>
    front.filter(
      (basket) => isLocked(basket, isPremium, isLoading) === wantLocked
    ).length;

  while (front.length < ROW_GLANCE_CAP && (unlocked.length > 0 || locked.length > 0)) {
    const unlockedCount = countInFront(false);
    const lockedCount = countInFront(true);

    let pick = null;
    if (unlockedCount >= MIX_TARGET_PER_TYPE && locked.length > 0) {
      pick = locked.shift();
    } else if (lockedCount >= MIX_TARGET_PER_TYPE && unlocked.length > 0) {
      pick = unlocked.shift();
    } else if (preferUnlocked) {
      pick = unlocked.length > 0 ? unlocked.shift() : locked.shift();
    } else {
      pick = locked.length > 0 ? locked.shift() : unlocked.shift();
    }

    if (!pick) break;
    front.push(pick);
    preferUnlocked = !preferUnlocked;
  }

  const frontIds = new Set(front.map((basket) => basket.id));
  const tail = ordered.filter((basket) => !frontIds.has(basket.id));
  return [...front, ...tail];
};

/** usePremium exposes isPremium as a function; tolerate a boolean too. */
const resolveIsPremium = (isPremium) =>
  typeof isPremium === 'function' ? Boolean(isPremium()) : Boolean(isPremium);

/**
 * Stable row order for the Baskets tab (call once per workspace load).
 * Pinned → newest updated_at; then least-recently-recalled; ties by mastery,
 * topic count, name. Free users then get a conversion mix in the first four.
 * First slot must be openable (≥2 topics, unlocked) when possible.
 */
export const orderBasketsForRow = (
  baskets,
  { isPremium, isLoading } = {}
) => {
  const opts = { isPremium, isLoading };
  let ordered = [...(baskets || [])].sort(compareForRow);
  if (ordered.length === 0) return ordered;

  // isPremium from usePremium is a function — never test it as a boolean.
  if (!resolveIsPremium(isPremium) && !isLoading) {
    ordered = applyFreeUserRowMix(ordered, opts);
  }

  return applyFirstSlotRule(ordered, opts);
};

/**
 * First non-anchor topic with a relation_description, else the first topic.
 */
export const pickStartTopicId = (basket) => {
  const topics = basket?.topics || [];
  if (topics.length === 0) return null;

  const withRelation = topics.find(
    (topic) => !topic.is_anchor && Boolean(topic.relation_description?.trim())
  );
  if (withRelation) return withRelation.id;

  return topics[0]?.id || null;
};
